import {
  Icon,
  IconButton,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  LocalShippingOutlined as LocalShippingIcon,
  ThumbUpOutlined as ThumbUpIcon,
} from '@material-ui/icons'
import clsx from 'clsx'
import sortBy from 'lodash/sortBy'
import {
  ButtonBack,
  ButtonNext,
  CarouselProvider,
  Slider,
  Slide,
} from 'pure-react-carousel'
import React, { useState } from 'react'
import Lightbox from 'react-image-lightbox'
import ReactPlayer from 'react-player'

import { useAuth } from 'src/modules/auth'
import { Button, Chip, SidePanel, Typography } from 'src/modules/ui'

import LeaveReviewModal from './LeaveReviewModal'
import SustainabilityInfo from './SustainabilityInfo'

const useStyles = makeStyles(theme => ({
  awardIcon: {
    width: 'auto',
  },
  body: {
    display: 'flex',
    padding: theme.spacing(1, 4, 4),
    flexDirection: 'column-reverse',
    [theme.breakpoints.up('tablet')]: {
      flexDirection: 'row',
      paddingTop: theme.spacing(1),
    },
  },
  chip: {
    backgroundColor: theme.palette.lightGreen.main,
    color: 'white',
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  content: {
    flexGrow: 1,
  },
  description: {
    marginBottom: theme.spacing(4),
    whiteSpace: 'pre-line',
  },
  photos: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: theme.spacing(4),
    '& .carousel__back-button, & .carousel__next-button': {
      background: 'transparent',
      border: 0,
      padding: 0,
      '& .carousel__back-button, & .carousel__next-button': {
        background: 'transparent',
        border: 0,
        padding: 0,
      },
    },
  },
  photosArrow: {
    color: theme.palette.lightGreen.main,
    '.carousel__back-button:disabled &, .carousel__next-button:disabled &': {
      color: theme.palette.text.secondary,
      '&:hover': {
        backgroundColor: 'transparent',
        cursor: 'auto',
      },
    },
  },
  photosCarousel: {
    flexGrow: 1,
    fontWeight: 'bold',
  },
  photoThumbnail: {
    backgroundColor: 'white',
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    display: 'block',
    objectFit: 'cover',
    height: '100%',
    margin: '0 auto',
    width: 'calc(100% - 16px)',
  },
  reviewsButton: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('mobile')]: {
      marginTop: 0,
    },
  },
  reviewsTitle: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('mobile')]: {
      flexDirection: 'row',
    },
  },
  review: {
    display: 'flex',
    marginBottom: theme.spacing(3),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  reviewIcon: {
    backgroundColor: theme.palette.lightGreen.main,
    borderRadius: '50%',
    color: 'white',
    marginRight: theme.spacing(1),
    marginTop: 2,
    padding: 4,
  },
  reviewBody: {
    flexGrow: 1,
    marginLeft: theme.spacing(1),
  },
  sidePanelContainer: {
    flexShrink: 0,
    marginLeft: 0,
    marginBottom: theme.spacing(4),
    width: 'auto',
    [theme.breakpoints.up('tablet')]: {
      marginLeft: theme.spacing(4),
      width: 250,
    },
  },
  video: {
    position: 'relative',
    paddingTop: '56.25%',
  },
  videoContainer: {
    maxWidth: 600,
    marginBottom: theme.spacing(4),
  },
  videoPlayer: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
}))

const AboutSupplier = ({ canLeaveReview, ...supplier }) => {
  const {
    awards,
    deliveryArea,
    description,
    photos,
    name,
    reviews,
    sustainability,
    videoUrl,
  } = supplier

  const classes = useStyles()
  const theme = useTheme()
  const aboveMobile = useMediaQuery(theme.breakpoints.up('mobile'))
  const aboveTablet = useMediaQuery(theme.breakpoints.up('tablet'))
  const aboveDesktop = useMediaQuery(theme.breakpoints.up('desktop'))
  const [photoIdx, setPhotoIdx] = useState(0)
  const [photoOpen, setPhotoOpen] = useState(false)
  const { user } = useAuth()

  let numSlides
  if (aboveDesktop) {
    numSlides = 4
  } else if (aboveTablet) {
    numSlides = 3
  } else if (aboveMobile) {
    numSlides = 2
  } else {
    numSlides = 1
  }

  const handleClickPhoto = idx => {
    setPhotoIdx(idx)
    setPhotoOpen(true)
  }

  const hasLeftReview = canLeaveReview
    ? reviews.some(r => r.user.id === user.id)
    : false

  return (
    <div className={classes.body}>
      <div className={classes.content}>
        <Typography className={classes.description}>{description}</Typography>
        {videoUrl && (
          <div className={classes.videoContainer}>
            <div className={classes.video}>
              <ReactPlayer
                className={classes.videoPlayer}
                controls
                url={videoUrl}
                width="100%"
                height="100%"
              />
            </div>
          </div>
        )}
        {photos.length > 0 && (
          <>
            <CarouselProvider
              totalSlides={photos.length}
              naturalSlideHeight={200}
              naturalSlideWidth={216}
              visibleSlides={numSlides}
            >
              <div className={classes.photos}>
                <ButtonBack>
                  <IconButton
                    component="div"
                    size="small"
                    className={classes.photosArrow}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                </ButtonBack>
                <div className={classes.photosCarousel}>
                  <Slider>
                    {photos.map(({ photoThumbnail }, idx) => (
                      <Slide
                        index={idx}
                        key={idx}
                        onClick={() => handleClickPhoto(idx)}
                      >
                        <img
                          src={photoThumbnail}
                          alt={name}
                          className={classes.photoThumbnail}
                        />
                      </Slide>
                    ))}
                  </Slider>
                </div>
                <ButtonNext>
                  <IconButton
                    component="div"
                    size="small"
                    className={classes.photosArrow}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </ButtonNext>
              </div>
            </CarouselProvider>
            {photoOpen && (
              <Lightbox
                mainSrc={photos[photoIdx].photo}
                nextSrc={photos[(photoIdx + 1) % photos.length].photo}
                prevSrc={
                  photos[(photoIdx + photos.length - 1) % photos.length].photo
                }
                onCloseRequest={() => setPhotoOpen(false)}
                onMovePrevRequest={() =>
                  setPhotoIdx((photoIdx + photos.length - 1) % photos.length)
                }
                onMoveNextRequest={() =>
                  setPhotoIdx((photoIdx + 1) % photos.length)
                }
              />
            )}
          </>
        )}
        <div className={classes.reviews}>
          <Typography
            className={classes.reviewsTitle}
            variant="h4"
            color="lightGreen"
          >
            Recommendations
            {canLeaveReview && (
              <LeaveReviewModal
                supplier={supplier}
                trigger={props => (
                  <Button
                    className={classes.reviewsButton}
                    disabled={hasLeftReview}
                    {...props}
                    color="lightGreen"
                  >
                    {hasLeftReview
                      ? 'Recommendation Left'
                      : 'Leave Recommendation'}
                  </Button>
                )}
              />
            )}
          </Typography>
          {reviews.length === 0 ? (
            <Typography color="textSecondary">
              No recommendations left yet...
            </Typography>
          ) : (
            reviews.map(({ createdAt, id, user, text }) => (
              <div className={classes.review} key={id}>
                <div>
                  <ThumbUpIcon className={classes.reviewIcon} />
                </div>
                <div className={classes.reviewBody}>
                  <Typography>{text}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {user.firstName} {user.lastName} - {user.jobTitle},{' '}
                    {user.organisation.name}
                  </Typography>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {(deliveryArea || awards.length > 0 || sustainability.length > 0) && (
        <div className={classes.sidePanelContainer}>
          <SidePanel>
            {deliveryArea && (
              <SidePanel.Item Icon={LocalShippingIcon} title="Delivery Areas">
                <Typography variant="body2">{deliveryArea}</Typography>
              </SidePanel.Item>
            )}
            {sustainability.length > 0 && (
              <SustainabilityInfo sustainability={sustainability} />
            )}
            {awards.length > 0 && (
              <SidePanel.Item
                Icon={({ className }) => (
                  <Icon
                    className={clsx(
                      className,
                      'fas fa-trophy',
                      classes.awardIcon
                    )}
                  />
                )}
                title="Awards"
              >
                {sortBy(awards, ['year', 'name']).map(
                  ({ id, name, year }, idx) => (
                    <Chip
                      label={`${name} (${year})`}
                      key={id}
                      size="small"
                      className={classes.chip}
                    />
                  )
                )}
              </SidePanel.Item>
            )}
          </SidePanel>
        </div>
      )}
    </div>
  )
}

export default AboutSupplier
